import {
  Card,
  TextInput,
  Button,
  Select,
  Label,
  Checkbox,
  Table,
  ButtonGroup,
  Datepicker,
} from "flowbite-react";
import { HiClock } from "react-icons/hi";
import {
  Wysiwyg,
  TagTextInput,
  FileManager,
  Drafts,
  DateTimePicker,
} from "../components";
import { useCallback, useEffect, useRef, useState } from "react";
import useNotes from "../hooks/useNotes";
import { useNavigate, useParams } from "react-router-dom";
import { useReducerAsState } from "../hooks/useReducerAsState";
import { SubmitableNote, Note } from "../types/notes";
import useCategories from "../hooks/useCategories";
import { Attachment } from "../types/attachment";
import { toast } from "react-toastify";
import useAuthors from "../hooks/useAuthors";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { noteSchema } from "../schemas/note.schema";
import { routes } from "../routes";
import { useAuth } from "../context/authContext";
import { Category } from "../types/categories";
import AutoSaveModel from "../models/auto.save.model";
import { DraftType } from "../types/drafts";
import { normalizeWyiwyg } from "../utils/parsers";
import { formatDate } from "../utils/formatters";

interface NoteState {
  title: string;
  excerpt: string;
  categories: Category[];
  published: number;
  keywords: Array<any>;
  description: string;
  attachment: Attachment | null;
  details: Note | null;
  author: number;
  autoSaveId: number;
  scheduledAt: string;
  showDateTimePicker: boolean;
}

const formatKeywords = (keywords: any) =>
  keywords.map((item: any) => {
    return {
      id: item.id.toString(),
      text: item.name,
    };
  });

const NoteDetailRoute = () => {
  const { id } = useParams();
  const { loading, findNote, updateNote, createNote } = useNotes();
  const { categories, fetchCategories } = useCategories();
  const { authors, fetchAuthors } = useAuthors();
  const [showFileManager, setShowFileManager] = useState<boolean>(false);
  const editorRef = useRef<any>(null);
  const imperativeEditorRef = useRef<any>(null);
  const { permissions } = useAuth();

  const canCreate = permissions.find((item) => item.slug === "crear-notas");
  const canEdit = permissions.find((item) => item.slug === "editar-notas");

  const navigate = useNavigate();

  const [state, setState] = useReducerAsState<NoteState>({
    title: "",
    excerpt: "",
    categories: [],
    published: 0,
    keywords: [],
    description: "",
    attachment: null,
    details: null,
    author: 0,
    autoSaveId: 0,
    scheduledAt: "",
    showDateTimePicker: false,
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
  } = useForm({
    resolver: yupResolver(noteSchema),
  });

  watch("isOtherAuthor");
  watch("published");
  watch("author");
  watch("categories");
  watch("title");
  watch("excerpt");
  watch("slug");
  watch("image");
  watch("scheduledAt");

  const fetchNoteDetails = async (id: number) => {
    const details = await findNote(id);
    if (!details) return;
    setValue("title", details.title);
    setValue("excerpt", details.excerpt);
    setValue("categories", details.categories);
    setValue("image", details.image ? details.image.id : 0);
    setValue("published", details.published);
    setValue("author", details.author.id);
    setValue("isOtherAuthor", details.author.id! > 0);
    setValue("slug", details.slug ?? "");
    setValue("scheduledAt", details.scheduled_at ?? "");

    setState({
      keywords:
        details.keywords.length > 0 ? formatKeywords(details.keywords) : [],
      description: details.description,
      attachment: details.image,
      details,
      categories: details.categories,
      scheduledAt: details.scheduled_at?.replaceAll(" ", "T") ?? "",
    });
  };

  const updateNoteContent = (key: any, value: string) =>
    setState({
      [key]: value,
    });

  const handleShowFileManager = () => setShowFileManager(!showFileManager);

  const handleSelectedFile = (file: Attachment) => {
    if (!file || file.type !== "image") {
      toast("Solamente se permiten imagenes para la imagen de la nota.", {
        type: "warning",
      });
      return;
    }
    setState({ attachment: file });
    setValue("image", file.id);
    handleShowFileManager();
  };

  const showDateTimePicker = (value: boolean) =>
    setState({ showDateTimePicker: value });

  const onSelectedScheduleDate = (dateTime: string) => {
    setState({ scheduledAt: dateTime, published: 0 });
    setValue("scheduledAt", dateTime);
    setValue("published", 0);
  };

  const onCategoryChange = (e: any, item: Category) => {
    const newCategories = e.target.checked
      ? [...state.categories, item]
      : state.categories.filter((p) => p.id !== item.id);
    setState({ categories: newCategories });
    setValue("categories", newCategories);
  };

  const save = async (data: any) => {
    try {
      if (editorRef && editorRef.current) {
        // author could be the current logged user or a new author from authors collection
        // if authorId == currentUser.id ? logged_user : other_author = 1
        const content = editorRef.current.getContent();
        if (content.length === 0) {
          toast("No olvides escribir el contenido de la nota", {
            type: "warning",
          });
          return;
        }

        // Define a regexp to match the sandbox attribute within iframe tags
        const sandboxRegexp = /(<iframe\b[^>]*\s)sandbox=["'].*?["']([^>]*>)/gi;

        // Replace the sandbox attribute in all matched iframe tags
        const updatedContent = content.replace(sandboxRegexp, "$1$2");

        // DC: remove slideshows paragraph & button, just leave comment from the content
        const normalizedContent = normalizeWyiwyg(updatedContent);

        const hasScheduleDate = data.scheduledAt && data.scheduledAt !== "";
        const scheduledAtDate =
          hasScheduleDate && data.scheduledAt.includes("T")
            ? data.scheduledAt.split("T")[0] +
              " " +
              data.scheduledAt.split("T")[1]
            : data.scheduledAt;

        const noteDetails: SubmitableNote = {
          title: data.title,
          excerpt: data.excerpt,
          description: normalizedContent,
          image: data.image,
          published: data.published,
          slug: data.slug,
          is_other_author: data.isOtherAuthor,
          author: data.author,
          categories: data.categories,
          keywords: state.keywords,
          scheduledAt: scheduledAtDate,
        };

        if (state.details === null) {
          if (canCreate) {
            const created = await createNote(noteDetails);
            toast(
              created
                ? "Nota creada correctamente."
                : "Ocurrió un error al crear la nota",
              {
                type: created ? "success" : "error",
              }
            );
            navigate(`${routes.DASHBOARD}/${routes.NOTES}`, {
              replace: true,
            });
            return;
          } else {
            toast("No tienes permiso para crear notas nuevas", {
              type: "warning",
            });
            return;
          }
        }

        if (canEdit) {
          noteDetails["id"] = state.details!.id;
          const updated = await updateNote(noteDetails);

          toast(
            updated
              ? "Nota guardada correctamente."
              : "Ocurrió un error al guardar la nota",
            {
              type: updated ? "success" : "error",
            }
          );
        } else {
          toast("No tienes permiso para actualizar nuevas", {
            type: "warning",
          });
          return;
        }
      }
    } catch (e) {
      console.log(e, "ERROR_FORM");
    }
  };

  const updateNoteContentFromDraft = (draft: DraftType) => {
    if (draft.item === undefined) return;
    const data = JSON.parse(draft.item);

    setTimeout(() => {
      setState({ attachment: data.attachment });

      setValue("title", data.title);
      setValue("excerpt", data.excerpt);
      setValue("categories", data.categories);
      setValue("image", data.image ? data.image.id : 0);
      setValue("published", data.published);
      setValue("author", data.author ? data.author.id : 0);
      setValue("isOtherAuthor", data.author && data.author.id! > 0);
      setValue("slug", data.slug ?? "");
      setValue("scheduledAt", data.scheduledAt ?? "");

      updateNoteContent("keywords", data.keywords);

      // DC: Todo update note content from draft
      if (editorRef && editorRef.current) {
        // parse slideshow ...
        editorRef.current.setContent(data.description);
        imperativeEditorRef.current.handleSlideshows(data.description);
      }
    }, 0);
  };

  const initialize = async () => {
    const draft = new AutoSaveModel();
    await draft.openDb();
    // DC: create empty draft
    const autoSaveId = await draft.createDraft();
    if (autoSaveId > 0) {
      setState({ autoSaveId });
    }

    await fetchCategories();
    await fetchAuthors({
      page: 1,
      limit: 999999,
    });

    if (!id) return;

    const noteId = parseInt(id);
    if (!isNaN(noteId)) {
      await fetchNoteDetails(noteId);
    }
  };

  const renderNoteLink = () => {
    if (!id || id === "create") {
      return (
        <TextInput
          placeholder="Escriba aquí"
          {...register("slug")}
          helperText={
            errors && errors.slug
              ? errors.slug.message
              : "Enlace de la nota. Se genera automáticamente si no se especifica. (Basado en el título)"
          }
          required
          color={errors && errors.slug ? "failure" : "gray"}
        />
      );
    }

    const categories = getValues("categories") ?? [];

    if (categories!.length === 0)
      return (
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
          Seleccione al menos una categoría
        </p>
      );

    const firstCategorySelected = categories[0];
    const prefix = `https://monclova.com/${firstCategorySelected.slug}`;
    const noteLink = `${prefix}/${getValues("slug")}`;

    return (
      <div className="w-full">
        <ButtonGroup className="w-full">
          <TextInput
            placeholder="Escriba aquí"
            {...register("slug")}
            required
            color={errors && errors.slug ? "failure" : "gray"}
            className="w-full"
            addon={prefix}
          />
          <Button
            onClick={() => {
              navigator.clipboard.writeText(noteLink).then(
                function () {
                  alert("Enlace copiado correctamente");
                },
                function (err) {
                  alert(`No se pudo copiar el enlace: ${err}`);
                }
              );
            }}
            color="gray"
          >
            Copiar
          </Button>
        </ButtonGroup>
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
          {errors && errors.slug
            ? errors.slug.message
            : "Enlace de la nota. Se genera automáticamente si no se especifica. (Basado en el título)"}
        </p>
      </div>
    );
  };

  const autoSaveNoteEvent = useCallback(async () => {
    if (state.autoSaveId <= 0) return;

    if (editorRef && editorRef.current) {
      const draft = new AutoSaveModel();
      await draft.openDb();
      // author could be the current logged user or a new author from authors collection
      // if authorId == currentUser.id ? logged_user : other_author = 1
      const content = editorRef.current.getContent();
      const updatedContent = normalizeWyiwyg(content);

      const noteDetails = {
        title: getValues("title"),
        excerpt: getValues("excerpt"),
        description: updatedContent,
        image: getValues("image"),
        attachment: state.attachment,
        published: getValues("published"),
        slug: getValues("slug"),
        is_other_author: getValues("isOtherAuthor"),
        author: getValues("author"),
        categories: getValues("categories"),
        keywords: state.keywords,
        draftType: "note",
        scheduledAt: getValues("scheduledAt") ?? "",
      };

      await draft.updateDraft(state.autoSaveId, JSON.stringify(noteDetails));
    }
  }, [getValues, state.attachment, state.autoSaveId, state.keywords]);

  useEffect(() => {
    if (state.autoSaveId > 0) {
      const interval = setInterval(async () => {
        await autoSaveNoteEvent();
      }, 10000);

      return () => clearInterval(interval);
    }
  }, [state.autoSaveId, autoSaveNoteEvent]);

  useEffect(() => {
    (async () => {
      await initialize();
    })();

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const message = "You have unsaved changes, do you really want to leave?";
      event.preventDefault();
      event.returnValue = message; // Standard for most browsers
      return message; // Some browsers require this
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup interval on component unmount
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <div className="m-4">
      <div className="mb-4">
        <div className="flex flex-row justify-between mb-4">
          <h1>Detalles de la nota</h1>
          <Button
            isProcessing={loading}
            disabled={loading}
            onClick={handleSubmit(save)}
          >
            Guardar nota
          </Button>
        </div>
        <Drafts onSelectDraft={(draft) => updateNoteContentFromDraft(draft)} />
      </div>
      <Card>
        <div className="flex flex-row justify-between gap-4">
          <div className="flex flex-1 flex-col">
            <div id="fileUpload" className="max-w-md mb-4">
              <div className="mb-2 block">
                <Label htmlFor="file" value="Imagen de la nota (1024 x 768)" />
              </div>
              {state.attachment && (
                <Card
                  className="max-w-sm mb-4"
                  imgAlt={state.attachment?.foot}
                  imgSrc={`${process.env.REACT_APP_UPLOADS}/${state.attachment?.url}`}
                >
                  <p className="font-normal text-gray-700 dark:text-gray-400">
                    {`${state.attachment?.source} ${
                      state.attachment?.source !== "" &&
                      state.attachment?.foot !== ""
                        ? "|"
                        : ""
                    } ${state.attachment?.foot}`}
                  </p>
                </Card>
              )}
              <Button
                color={state.attachment ? "failure" : "blue"}
                onClick={handleShowFileManager}
              >
                {state.attachment ? "Cambiar imagen" : "Elija una imagen"}
              </Button>
              {errors && errors.image && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                  {errors.image.message}
                </p>
              )}
            </div>

            <div className="flex flex-col mb-2">
              <Label className="mb-2">Título</Label>
              <TextInput
                placeholder="Escriba aquí"
                {...register("title")}
                helperText={errors && errors.title ? errors.title.message : ""}
                required
                color={errors && errors.title ? "failure" : "gray"}
              />
            </div>
            <div className="flex flex-col mb-2">
              <Label className="mb-2">Descripción corta</Label>
              <TextInput
                placeholder="Escriba aquí"
                {...register("excerpt")}
                helperText={
                  errors && errors.excerpt ? errors.excerpt.message : ""
                }
                required
                color={errors && errors.excerpt ? "failure" : "gray"}
              />
            </div>

            <div className="flex flex-col mb-0 w-full mb-2">
              <Label className="mb-2">Palabras clave</Label>
              <TagTextInput
                placeholder="Escriba aquí"
                helperText="Palabras que ayudan a relacionar esta nota con otras con las mismas palabras."
                value={state.keywords}
                onChange={(e) => updateNoteContent("keywords", e)}
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <div className="flex flex-col mb-2">
              <Label className="mb-2">Enlace de la nota</Label>
              {renderNoteLink()}
            </div>

            <div className="flex flex-col mb-4">
              {errors && errors.categories
                ? errors.categories.message && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-500">
                      {errors.categories.message}
                    </p>
                  )
                : null}
              <div className="mb-2 block">
                <Label htmlFor="category" value="Categorías" />
              </div>
              <div className="h-60 overflow-y-scroll">
                <Table className="bg-white-200 dark:bg-slate-200">
                  <Table.Body>
                    {categories &&
                      categories.map((item) => (
                        <Table.Row key={item.id}>
                          <Table.Cell className="p-0 pb-2">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={item.slug}
                                onChange={(e) => onCategoryChange(e, item)}
                                checked={state.categories.some(
                                  (p) => p.id === item.id
                                )}
                              />
                              <Label htmlFor={item.slug} className="flex">
                                {item.name}
                              </Label>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                  </Table.Body>
                </Table>
              </div>
            </div>

            <hr className="mb-4" />

            <div className="flex items-start flex-col gap-2 mb-4">
              <Button color="blue" onClick={() => showDateTimePicker(true)}>
                <HiClock className="h-5 w-5 mr-2" />
                Programar nota
              </Button>
              {state.scheduledAt.length > 0 && (
                <span className="text-xs font-semibold dark:text-white">
                  Programada para : {formatDate(state.scheduledAt)}
                </span>
              )}
            </div>

            <div className="flex flex-col max-w-md flex-1 mb-4">
              <div className="mb-2 block">
                <Label htmlFor="published" value="Publicada" />
              </div>
              <Select
                id="published"
                required
                {...register("published")}
                helperText={
                  errors && errors.published ? errors.published.message : ""
                }
                color={errors && errors.published ? "failure" : "gray"}
              >
                <option value="-1">- Elija una opción -</option>
                <option value="1">Si</option>
                <option value="0">No</option>
              </Select>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Checkbox id="isOtherAuthor" {...register("isOtherAuthor")} />
              <Label htmlFor="isOtherAuthor">¿Cambiar de autor?</Label>
            </div>

            {getValues("isOtherAuthor") === true && (
              <div className="flex flex-col max-w-md flex-1 mb-4">
                <div className="mb-2 block">
                  <Label htmlFor="author" value="Autor" />
                </div>
                <Select
                  id="author"
                  required
                  {...register("author")}
                  helperText={
                    errors && errors.author ? errors.author.message : ""
                  }
                  color={errors && errors.author ? "failure" : "gray"}
                >
                  <option value="-1">- Elija una opción -</option>
                  {authors &&
                    authors.map((author) => (
                      <option key={author.id} value={author.id}>
                        {author.name}
                      </option>
                    ))}
                </Select>
              </div>
            )}
          </div>
        </div>

        <Wysiwyg
          label={"Descripción"}
          ref={imperativeEditorRef}
          editorRef={editorRef}
          content={state.description || ""}
        />
      </Card>

      {showFileManager && (
        <FileManager
          open={showFileManager}
          onClose={handleShowFileManager}
          onSelect={handleSelectedFile}
        />
      )}
      {state.showDateTimePicker && (
        <DateTimePicker
          isOpen={state.showDateTimePicker}
          date={state.scheduledAt.split("T")[0]}
          time={state.scheduledAt.split("T")[1]}
          title="Programar nota"
          onClose={() => showDateTimePicker(false)}
          onSelectedDateTime={onSelectedScheduleDate}
        />
      )}
    </div>
  );
};

export default NoteDetailRoute;
